import type { ConfiguratorPageDefinition, StepHardcodedData } from "~/Configurators/ConfiguratorPageDefinition";
import * as RailCstic from "./TKLTRailsCstics";
import { ConfiguratorType } from "~/Configurators/ConfiguratorType";
import { TKLT_RAIL_CONFIGURATOR_PRODUCT_CODE } from "~/Configurators/constants";
import { BASE_DESIGNATION_SEPARATOR } from "~/Configurators/Linear/constants";
import {
  type ConflictKmat,
  type Kmat,
  type LinearConfiguration,
  type LinearConfigurationUpdate,
  type LinearCstic,
  type LinearSubstep,
  SimpleCsticId,
} from "~/Configurators/Linear/models";
import { getHighestVisitedStep } from "~/Configurators/Linear/redux/selectors";
import { findSelectedTileIndex, fromExtraValidationToDirtyParamName } from "~/Configurators/Linear/utils";
import { sendRailAddToCart } from "~/Configurators/Rail/api/sendRailAddToCart";
import { sendRailUpdate } from "~/Configurators/Rail/api/sendRailConfiguratorUpdate";
import { sendRailResetConfigurator } from "~/Configurators/Rail/api/sendRailResetConfigurator";
import { sendRailMiscChange, sendRailStepChange } from "~/Configurators/Rail/api/sendRailStepChange";
import { sendRailDataReset } from "~/Configurators/Rail/api/sendRailStepReset";
import { getMainKmat } from "~/Configurators/Shaft/common/commonShaftHelpers";
import type { RootState } from "~/redux/store";
import { csticHasValue, getCsticsValue, join } from "~/Configurators/common/v2/helper";
import { TKLTRailSummaryData } from "~/Configurators/Rail/v2/configuration/tklt/summary/TKLTRailSummaryData";
import { getExtraValidationParamName } from "~/Configurators/Linear/namingUtils";
import { makeSendStepCompletedEvent, makeSummaryDataProvider } from "~/Configurators/Shaft/shaftGaHelper";
import { grabXmlTranslation } from "~/Configurators/grabExternalTranslation";
import { fetchSimpleSubstepValue } from "~/Configurators/Linear/components/fetchSimpleSubstepValue";
import { toMapFn } from "~/utils/reducers";
import { prepareSummaryDataName, prepareSummaryDataValue } from "~/Configurators/common/v2/gaHelper";
import type { SummaryData } from "~/Configurators/Shaft/SummaryDataProvider";
import { getStep2SummaryData, getStep3SummaryData, getStep4SummaryData } from "./summary/TKLTSummaryDataProvider";

const RAIL_CONFIGURATOR_STEP_DATA: StepHardcodedData[] = [
  {
    resetLabelKey: "",
    resetRequiresConfirmation: true,
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const summaryDataProvider = makeSummaryDataProvider(state);
      const kmat = getMainKmat({ kmats: state.linearConfigurator.kmats });

      const extraData: SummaryData[] = [RailCstic.GuidewayDesign, RailCstic.Size].map((csticName) => {
        const cstic = kmat.cstics[csticName];
        const csticValue = cstic?.values.find((item) => item.value == cstic?.value);
        return {
          name: summaryDataProvider.t(cstic.label),
          value: summaryDataProvider.t(csticValue?.label ?? ""),
        };
      });

      const seriesValue = grabXmlTranslation(
        summaryDataProvider,
        fetchSimpleSubstepValue(state.linearConfigurator.substeps.Series) ?? "",
      );
      extraData[0].value = seriesValue;

      const selectionData = {
        ...toMapFn(extraData, prepareSummaryDataName, prepareSummaryDataValue),
      };

      sendStepCompletedEvent(summaryDataProvider.getGaStepName(1), selectionData);
    },
    gaName: "",
  },

  {
    resetLabelKey: "",
    resetRequiresConfirmation: true,
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const summaryDataProvider = makeSummaryDataProvider(state);
      const step2Data = getStep2SummaryData(summaryDataProvider) ?? [];

      const selectionData = toMapFn(step2Data, prepareSummaryDataName, prepareSummaryDataValue);

      sendStepCompletedEvent(summaryDataProvider.getGaStepName(2), selectionData);
    },
    gaName: "",
  },

  {
    resetLabelKey: "",
    resetRequiresConfirmation: true,
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const summaryDataProvider = makeSummaryDataProvider(state);
      const step2Data = getStep3SummaryData(summaryDataProvider) ?? [];

      const selectionData = toMapFn(step2Data, prepareSummaryDataName, prepareSummaryDataValue);

      sendStepCompletedEvent(summaryDataProvider.getGaStepName(3), selectionData);
    },
    gaName: "",
  },

  {
    resetLabelKey: "",
    resetRequiresConfirmation: true,
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const summaryDataProvider = makeSummaryDataProvider(state);
      const step2Data = getStep4SummaryData(summaryDataProvider) ?? [];

      const selectionData = toMapFn(step2Data, prepareSummaryDataName, prepareSummaryDataValue);

      sendStepCompletedEvent(summaryDataProvider.getGaStepName(4), selectionData);
    },
    gaName: "",
  },
];

export const TKLT_RAIL_DEFINITION: ConfiguratorPageDefinition = {
  type: ConfiguratorType.Rail,
  productCode: TKLT_RAIL_CONFIGURATOR_PRODUCT_CODE,
  translationKeys: {
    pageTitle: "configurator.rail.name",
  },
  apiCalls: {
    update: sendRailUpdate,
    reset: sendRailResetConfigurator,
    stepChange: sendRailStepChange,
    stepReset: sendRailDataReset,
    miscChange: sendRailMiscChange,
    addToCart: sendRailAddToCart,
  },
  summary: TKLTRailSummaryData,
  isAddToCartEnabled: (state: RootState) => {
    const kmats = state.linearConfigurator.kmats;
    const conflicts = state.linearConfigurator.conflicts;

    const validations = [0, 1, 2, 3].map(
      (step) =>
        state.linearConfigurator.steps[step].substeps?.every(
          (substep) =>
            validateFromDefinition(state.linearConfigurator.substeps[substep] as LinearSubstep, kmats, conflicts)
              .substepValid,
        ) ?? false,
    );

    const addToCartEnabled = validations.every((validation) => validation);

    return addToCartEnabled;
  },
  isSaveEnabled: (state: RootState) => {
    const step = getHighestVisitedStep(state);
    const kmat = getMainKmat({ kmats: state.linearConfigurator.kmats });
    const hasLengthSet = csticHasValue(kmat, RailCstic.GuidewayLength);

    return step >= 1 && hasLengthSet;
  },
  stepHardcodedData: RAIL_CONFIGURATOR_STEP_DATA,
  isConflictAvoidable: (csticName) => {
    return [RailCstic.GuidewayLength, RailCstic.AL, RailCstic.AR].includes(csticName);
  },
  generateDesignation: (kmats) => {
    const kmat = getMainKmat({ kmats });

    const [series, sizeCode, version] = getCsticsValue(
      kmat,
      RailCstic.DesignType_Series,
      RailCstic.DesignType_SizeCode,
      RailCstic.DesignType_Version,
    );

    return join("-", join("", series, sizeCode), version) || BASE_DESIGNATION_SEPARATOR;
  },
  gaConfiguratorName: "kllt-rail",
  getGaStepName: (step) => RAIL_CONFIGURATOR_STEP_DATA[step]?.gaName,
  modelAndUpdatePreprocessor: (model) => {
    const kmat = getMainKmat({ kmats: model.kmats });
    const cstics = kmat.cstics;

    const lengthCstic = cstics[RailCstic.GuidewayLength];
    if (lengthCstic) {
      lengthCstic.label = "configurator.rail.cstic.guidewayLength.name";
    }

    const alCstic = cstics[RailCstic.AL];
    if (alCstic) {
      alCstic.label = "configurator.rail.section.al.name";
    }

    const arCstic = cstics[RailCstic.AR];
    if (arCstic) {
      arCstic.label = "configurator.rail.section.ar.name";
    }

    const partSizingCstic = cstics[RailCstic.PartSizing];
    if (partSizingCstic) {
      partSizingCstic.label = "configurator.rail.cstic.partSizing.name";
      partSizingCstic.values.forEach((val) => {
        val.label = `configurator.rail.cstic.partSizing.${val.value}.name`;
      });
    }

    const partAmountCstic = cstics[RailCstic.PartAmount];
    if (partAmountCstic) {
      partAmountCstic.label = "configurator.rail.cstic.partAmount.name";
    }

    return model;
  },
  postDefinitionProcessor: (model, dto) => {
    model.allowedSteps = calculateAllowedSteps(model);
    return model;
  },
  canQueryAvailability: (kmats) => {
    const cstics = getMainKmat({ kmats }).cstics;

    const hasAllRequiredCstics = [
      RailCstic.GuidewayDesign,
      RailCstic.Size,
      RailCstic.Coating,
      RailCstic.Accuracy,
    ].every((cstic) => !!cstics[cstic].value);

    return hasAllRequiredCstics;
  },
} as const;

const EMPTY_FAKE_CSTIC: LinearCstic = {
  key: "",
  name: "",
  label: "",
  value: "",
  groupId: "",
  values: [],
  type: "",
  info: "",
  description: "",
  readonly: false,
  fake: true,
};

function calculateAllowedSteps(model: LinearConfiguration | LinearConfigurationUpdate): boolean[][] {
  const kmats = model.kmats;
  const conflicts = "conflictedCstics" in model ? model.conflictedCstics : [];

  const [step1Valid, step2Valid, step3Valid, step4Valid] = [0, 1, 2, 3].map(
    (step) =>
      model.steps[step].substeps?.every(
        (substep) => validateFromDefinition(model.substeps[substep] as LinearSubstep, kmats, conflicts).substepValid,
      ) ?? false,
  );

  const canVisitPage2 = step1Valid;
  const canVisitPage3 = step2Valid && canVisitPage2;
  const canVisitPage4 = step3Valid && canVisitPage3;
  const canVisitPage5 = step4Valid && canVisitPage4;

  const allowedSteps = [
    [true, canVisitPage2, canVisitPage3, canVisitPage4, canVisitPage5],
    [true, true, canVisitPage3, canVisitPage4, canVisitPage5],
    [true, true, true, canVisitPage4, canVisitPage5],
    [true, true, true, true, canVisitPage5],
    [true, true, true, true, true],
  ];

  return allowedSteps;
}

function validateFromDefinition(substep: LinearSubstep, kmats: Record<string, Kmat[]>, conflicts: ConflictKmat[]) {
  const mainKmat = getMainKmat({ kmats });
  const conflictedCstics = getConflictCsticNames(conflicts);
  // Line copied from linear reducer
  const selectedTile = findSelectedTileIndex(substep, kmats);

  let valid = true;
  const extraValidations: Record<string, boolean> = {};
  const additionalData: Record<string, boolean> = {};

  if (!substep.isVisible) {
    return {
      substepValid: true,
      extraValidations: {},
      additionalData: {},
    };
  }

  if (substep.tiles.length || substep.groupedTiles.length) {
    // Validate that a tile is selected
    valid = selectedTile !== null;
  }
  Object.values(substep.inputCstics).forEach((cstic) => {
    let csticValid = true;

    if (cstic.visible) {
      csticValid = !conflictedCstics.includes(cstic.name);
    }

    if (cstic.visible && cstic.required) {
      csticValid = csticValid && csticHasValue(mainKmat, cstic.name);
    }
    valid = valid && csticValid;
    const key = getExtraValidationParamName(SimpleCsticId(cstic.name));
    extraValidations[key] = csticValid;
    const dirtyKey = fromExtraValidationToDirtyParamName(key);
    additionalData[dirtyKey] = true;
  });

  return {
    substepValid: valid,
    extraValidations,
    additionalData,
  };
}

function getConflictCsticNames(conflicts: ConflictKmat[]): string[] {
  return conflicts.flatMap((conflict) => conflict.cstics.map((cstic) => cstic.name));
}

const sendStepCompletedEvent = makeSendStepCompletedEvent(TKLT_RAIL_DEFINITION);
